<template>
  <div
    class="full-slide-asset"
    :class="{
      'full-slide-asset--fullscreen': isFullscreen,
    }"
    :data-asset-id="asset && asset.id"
    :id="`full-slide-${asset && asset.id}`"
  >
    <ModuleError
      v-if="asset && asset.error"
      :error="asset.error"
      :asset-id="asset.id"
    />
    <MediaViewer
      ref="media"
      v-else-if="asset && asset.type"
      :asset-group="asset.group"
      :asset-id="asset.id"
      :autoplay="isAutoplay"
      :loop="loopVideo"
      :start-time="durationToSeconds('page.options.video_start')"
      :stop-time="durationToSeconds('page.options.video_end')"
      :thumbnail-url="assetThumbnailUrl"
      :media-url="mediaUrl"
      :module-data="{
        options: {
          image_settings: 'fill',
          layout: 'image',
        },
      }"
      :title="asset.title"
      :description="asset.description"
      :player-link="usePlayer ? playerLink : ''"
      :type="asset.type.name"
      :icon-id="iconId"
      :scale="scale"
      :video="video"
      :mode="mode"
      :is-displayed="isDisplayed"
      :theme-name="themeName"
      @video-ended="onVideoEnded"
    />
    <LoadingIndicator
      v-else
      class="full-slide-asset__loading-indicator"
      absolute
      transparent
    />
    <EditorMenu
      ref="editorMenu"
      v-if="showEditMenu"
      :scale="scale"
      :trigger-element="$el"
      :trigger-padding="0"
      :append-to-editor="true"
      :positions="['bottom']"
      box-name="editor"
      id-prefix="full-slide-asset-menu"
      @position-changed="onEditorMenuPositionChanged"
    >
      <FullSlideAssetMenu
        :asset="asset"
        :page="page"
        :view-in-content-url="viewInContentUrl"
        :tooltips-position="editorMenuTooltipDisplayTop ? 'block-start' : 'block-end'"
      />
    </EditorMenu>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { get } from 'lodash-es';
import { mapState, mapActions } from 'pinia';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import ModuleError from '@/components/modules/ModuleError.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import MediaViewer from '@/components/MediaViewer.vue';
import EditorMenu from '@/components/EditorMenu.vue';
import { MP3_GENERAL_AUDIO_FORMAT_ID } from '@/lib/constants/mediaFileFormatIds';
import { bus } from '@/lib/eventBus';
import assetMixin from '@/mixins/asset';
import thumbnailLoader from '@/mixins/thumbnailLoader';
import * as types from '@/lib/constants/store';
import {
  useAssetStore,
  useEditorStore,
} from '@/stores';
import { durationToSeconds } from '../../lib/utils';

// This is only used in EDIT mode
const FullSlideAssetMenu = defineAsyncComponent(() => import('@/components/full-slide-asset/FullSlideAssetMenu.vue'));

export default {
  name: 'FullSlideAsset',
  components: {
    EditorMenu,
    FullSlideAssetMenu,
    LoadingIndicator,
    MediaViewer,
    ModuleError,
  },
  mixins: [
    assetMixin, // handles pulling thumbnails urls from an asset
    thumbnailLoader, // handles finding valid thumbnails
  ],
  props: {
    page: {
      type: Object,
      default: null,
    },
    scale: {
      type: Number,
      default: 1,
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'VIEW',
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editorMenuTooltipDisplayTop: true,
      isFullSlideAsset: true, // used in the asset mixin
      space5x: parseInt(nebulaTokens.space['5x'], 10),
    };
  },
  created() {
    bus.on('editor-canvas-carousel:start-page-change-animation', this.onStartPageChangeAnimation);
  },
  beforeUnmount() {
    bus.off('editor-canvas-carousel:start-page-change-animation', this.onStartPageChangeAnimation);
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_NEXT_PAGE,
    ]),
    durationToSeconds,
    onStartPageChangeAnimation() {
      /*
        When this slide is animating out of view, ensure
        that the audio or video is paused.
      */
      const { media } = this.$refs;
      if (media) {
        media.pause();
      }
    },
    onVideoEnded() {
      if (this.autoAdvance) {
        this[types.VIEW_NEXT_PAGE]();
      }
    },
    onEditorMenuPositionChanged(context) {
      this.$nextTick(() => {
        const editorMenu = this.$refs?.editorMenu?.$el;
        if (editorMenu) {
          const menuRect = editorMenu.getBoundingClientRect();
          this.editorMenuTooltipDisplayTop = (menuRect.top - this.space5x)
            >= context.selected.box.top;
        }
      });
    },
  },
  watch: {
    isDisplayed() {
      /*
        When this slide is no longer displayed (in carousel but not visible),
        then reset audio and video to beginning.
      */
      if (!this.isDisplayed) {
        const { media } = this.$refs;
        if (media) {
          media.reset();
        }
      }
    },
  },
  computed: {
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useEditorStore, [
      'currentPageId',
      'currentModalCanvasId',
    ]),
    showEditMenu() {
      return this.mode === 'EDIT' && this.asset?.group === 'video'
        && this.page?.id === this.currentPageId
        && !this.currentModalCanvasId;
    },
    asset() {
      return this.assets[this.assetId];
    },
    assetId() {
      return this.page?.options?.asset_id;
    },
    loopVideo() {
      const loop = this.isFullscreen && get(this, 'page.options.loop_video', false);
      return loop && this.isDisplayed;
    },
    autoAdvance() {
      const autoAdvance = this.isFullscreen && get(this, 'page.options.auto_advance', false);
      return autoAdvance && this.isDisplayed;
    },
    isAutoplay() {
      const autoPlay = !!get(this, 'page.options.auto_play', false);
      return autoPlay && this.isDisplayed;
    },
    isLoading() {
      return !this.asset || this.thumbnailIsLoading;
    },
    mediaUrl() {
      // For audio, return the associated media file
      if (this.asset.group === 'audio') {
        const audioFile = this.asset.media_files
          .find((mediaFile) => mediaFile.format_id === MP3_GENERAL_AUDIO_FORMAT_ID);

        return audioFile && audioFile.url;
      }

      return null;
    },
    iconId() {
      return `asset-${this.asset.type.name.toLowerCase().replace(/ /g, '-')}`;
    },
    video() {
      if (this.asset.group !== 'video' || !this.assetVideoMediaFiles) return undefined;

      return {
        ...this.asset,
        media_files: this.assetVideoMediaFiles,
        closed_captions: this.assetClosedCaptions,
        duration_in_seconds: this.asset.duration,
      };
    },
    themeName() {
      return this.page?.options?.theme?.name;
    },
  },
};
</script>

<style lang="stylus">
.full-slide-asset {
  bottom: 0;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 3; // Should display above the editor canvas

  &:not(.full-slide-asset--fullscreen) {
    border-radius: $nebula-border-radius-small;
  }

  &--fullscreen {
    .asset-module__image {
      border-radius: 0;
    }
  }

  &__loading-indicator {
    pointer-events: none;
  }
}
</style>
